/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-nested-ternary */
import React, { useState, useMemo } from 'react';
import { Box, SimpleGrid, Spinner, Text, useDisclosure, Flex } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { createSearchParams, useLocation, useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { User } from 'phosphor-react';
import { ReturnButton } from '../../../../../components/buttons/ReturnButton';
import type { IData, IPagination, ISelect } from '../../../../../types/shared';
import { useCompanies } from '../../../../../hooks/useCompanies';
import type { ICompany, ITaxRegime } from '../../../../../hooks/useCompanies';
import { cleanFilters } from '../../../../../utils/HandleFunctions';
import { Paginator } from '../../../../../components/paginator';
import { toCurrency } from '../../../../../utils/FormatFunctions';
import { TaxRegimeSummaryCards } from '../../taxRegime/components/TaxRegimeSummaryCards';
import { TaxRegimeDetailModal } from '../../taxRegime/components/TaxRegimeDetailModal';
import { TaxRegimeTable } from '../../taxRegime/components/TaxRegimeTable';
import { TaxRegimeCard } from '../../taxRegime/components/TaxRegimeCard';
import { TaxRegimeFilters } from '../../taxRegime/components/TaxRegimeFilters';

export function ModulesHomeSupervisorPartnerCompaniesPage() {
  const { getTaxRegime, getQuebrasTaxRegimeTotais } = useCompanies();
  const {
    isOpen: isDetailModalOpen,
    onOpen: onDetailModalOpen,
    onClose: onDetailModalClose,
  } = useDisclosure();
  const [selectedCompany, setSelectedCompany] = useState<ITaxRegime | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [searchMode, setSearchMode] = useState<'ID' | 'Nome' | 'CNPJ'>('ID');

  const { itemId } = useParams();
  const location = useLocation();
  const [partnerData] = useState(location.state?.company);

  const [searchParams] = useSearchParams();
  const dateStart = searchParams.get('date_start');
  const dateEnd = searchParams.get('date_end');

  const [filters, setFilters] = useSearchParams(
    createSearchParams({
      page: '1',
      size: '50',
      search: '',
      socio_id: itemId ?? '',
      date_start: dateStart ?? moment().startOf('year').format('YYYY-MM-DD'),
      date_end: dateEnd ?? moment().endOf('year').format('YYYY-MM-DD'),
    }),
  );

  // Calcular filtros ativos para indicadores visuais
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.get('id')) count += 1;
    if (filters.get('cidade')) count += 1;
    if (filters.get('uf')) count += 1;
    if (filters.get('grupo_de_empresas')) count += 1;
    if (filters.get('tags')) count += 1;
    if (filters.get('comercial')) count += 1;
    if (filters.get('contabil')) count += 1;
    if (filters.get('financeiro')) count += 1;
    if (filters.get('contas_a_pagar')) count += 1;
    if (filters.get('encerramento_anual')) count += 1;
    if (filters.get('fiscal')) count += 1;
    if (filters.get('imposto_de_renda')) count += 1;
    if (filters.get('integracao_contabil')) count += 1;
    if (filters.get('integracao_fiscal')) count += 1;
    if (filters.get('pessoal')) count += 1;
    if (filters.get('relacionamento_com_cliente')) count += 1;
    if (filters.get('societario')) count += 1;
    return count;
  }, [filters]);

  // Função para aplicar os filtros avançados quando clica no botão "Filtrar"
  const handleApplyFilters = (values: {
    companies?: ICompany;
    cidade?: ISelect<string>;
    uf?: ISelect<string>;
    grupo_de_empresas?: ISelect<string>;
    tags?: ISelect<string>;
    comercial?: ISelect<string>;
    contabil?: ISelect<string>;
    financeiro?: ISelect<string>;
    contas_a_pagar?: ISelect<string>;
    encerramento_anual?: ISelect<string>;
    fiscal?: ISelect<string>;
    imposto_de_renda?: ISelect<string>;
    integracao_contabil?: ISelect<string>;
    integracao_fiscal?: ISelect<string>;
    pessoal?: ISelect<string>;
    relacionamento_com_cliente?: ISelect<string>;
    societario?: ISelect<string>;
  }) => {
    const newFilters = new URLSearchParams();

    // Mantém filtros básicos
    newFilters.set('page', '1');
    newFilters.set('size', '50');
    newFilters.set('search', filters.get('search') || '');
    // Mantém o regime_tributario se existir, caso contrário não define (null)
    const regime = filters.get('regime_tributario');
    if (regime) {
      newFilters.set('regime_tributario', regime);
    }
    newFilters.set('socio_id', itemId ?? '');
    newFilters.set(
      'date_start',
      filters.get('date_start') || moment().startOf('year').format('YYYY-MM-DD'),
    );
    newFilters.set(
      'date_end',
      filters.get('date_end') || moment().endOf('year').format('YYYY-MM-DD'),
    );

    // Aplica filtros avançados
    if (values.companies?.external_id) newFilters.set('id', values.companies.external_id);
    if (values.cidade?.value) newFilters.set('cidade', values.cidade.value);
    if (values.uf?.value) newFilters.set('uf', values.uf.value);
    if (values.grupo_de_empresas?.value)
      newFilters.set('grupo_de_empresas', values.grupo_de_empresas.value);
    if (values.tags?.value) newFilters.set('tags', values.tags.value);
    if (values.comercial?.value) newFilters.set('comercial', values.comercial.value);
    if (values.contabil?.value) newFilters.set('contabil', values.contabil.value);
    if (values.financeiro?.value) newFilters.set('financeiro', values.financeiro.value);
    if (values.contas_a_pagar?.value) newFilters.set('contas_a_pagar', values.contas_a_pagar.value);
    if (values.encerramento_anual?.value)
      newFilters.set('encerramento_anual', values.encerramento_anual.value);
    if (values.fiscal?.value) newFilters.set('fiscal', values.fiscal.value);
    if (values.imposto_de_renda?.value)
      newFilters.set('imposto_de_renda', values.imposto_de_renda.value);
    if (values.integracao_contabil?.value)
      newFilters.set('integracao_contabil', values.integracao_contabil.value);
    if (values.integracao_fiscal?.value)
      newFilters.set('integracao_fiscal', values.integracao_fiscal.value);
    if (values.pessoal?.value) newFilters.set('pessoal', values.pessoal.value);
    if (values.relacionamento_com_cliente?.value)
      newFilters.set('relacionamento_com_cliente', values.relacionamento_com_cliente.value);
    if (values.societario?.value) newFilters.set('societario', values.societario.value);

    setFilters(newFilters);
  };

  const handleSearchChange = (search: string, mode: 'ID' | 'Nome' | 'CNPJ') => {
    const newFilters = new URLSearchParams(filters);
    newFilters.delete('search');
    newFilters.delete('cnpj');
    newFilters.delete('external_id');

    if (search) {
      switch (mode) {
        case 'ID':
          newFilters.set('external_id', search);
          break;
        case 'Nome':
          newFilters.set('search', search);
          break;
        case 'CNPJ':
          newFilters.set('cnpj', search.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
          break;
        default:
          break;
      }
    }
    newFilters.set('page', '1');
    setFilters(newFilters);
  };

  const handleSelectRegime = (regime: 'lucro_presumido' | 'simples_nacional') => {
    const newFilters = new URLSearchParams(Object.fromEntries(filters));
    newFilters.set('page', '1');
    // Se clicar no mesmo regime que está selecionado, remove o filtro (desseleciona)
    const currentRegime = filters.get('regime_tributario') || 'simples_nacional';
    if (currentRegime === regime) {
      newFilters.delete('regime_tributario');
    } else {
      newFilters.set('regime_tributario', regime);
    }
    setFilters(newFilters);
  };

  const handleRowClick = (company: ITaxRegime) => {
    setSelectedCompany(company);
    onDetailModalOpen();
  };

  // Função para limpar todos os filtros
  const handleClearFilters = () => {
    const baseParams: Record<string, string> = {
      page: '1',
      size: '50',
      search: filters.get('search') || '',
      socio_id: itemId ?? '',
      date_start: filters.get('date_start') || moment().startOf('year').format('YYYY-MM-DD'),
      date_end: filters.get('date_end') || moment().endOf('year').format('YYYY-MM-DD'),
    };
    const regime = filters.get('regime_tributario');
    if (regime) {
      baseParams.regime_tributario = regime;
    }
    const clearedFilters = createSearchParams(baseParams);

    setFilters(clearedFilters);
  };

  // Obter o ano atual do filtro
  const currentFilterYear = moment(
    filters.get('date_start') ?? moment().format('YYYY-MM-DD'),
  ).year();

  // Funções para navegar entre anos
  const handlePrevYear = () => {
    const prevYear = currentFilterYear - 1;
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: moment().year(prevYear).startOf('year').format('YYYY-MM-DD'),
        date_end: moment().year(prevYear).endOf('year').format('YYYY-MM-DD'),
        page: '1',
      }),
    );
  };

  const handleNextYear = () => {
    const nextYear = currentFilterYear + 1;
    if (nextYear <= moment().year()) {
      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: moment().year(nextYear).startOf('year').format('YYYY-MM-DD'),
          date_end: moment().year(nextYear).endOf('year').format('YYYY-MM-DD'),
          page: '1',
        }),
      );
    }
  };

  const handleSelectYear = (year: number) => {
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: moment().year(year).startOf('year').format('YYYY-MM-DD'),
        date_end: moment().year(year).endOf('year').format('YYYY-MM-DD'),
        page: '1',
      }),
    );
  };

  // Filtros para o regime atual (pode ser null se nenhum estiver selecionado)
  const currentRegime = filters.get('regime_tributario') || null;

  const { data: dataTaxRegimeList, isFetching: isFetchingTaxRegimeList } = useQuery<
    IData<ITaxRegime[]>
  >({
    queryKey: ['taxRegimeList-partner', cleanFilters(Object.fromEntries([...Array.from(filters)]))],
    queryFn: getTaxRegime,
  });

  const { items: taxRegimeList, ...pagination } = dataTaxRegimeList?.data ?? {
    items: [],
    ...({} as IPagination),
  };

  // Buscar totais de empresas usando endpoint de totais (mais eficiente)
  // Este endpoint já retorna ambos os totais (simples_nacional e lucro_presumido) em uma única requisição
  const filtersForTotals = useMemo(() => {
    const baseFilters = Object.fromEntries([...Array.from(filters)]);
    // Remove campos que não são necessários para o endpoint de totais
    delete baseFilters.page;
    delete baseFilters.size;
    delete baseFilters.regime_tributario; // Buscar ambos os regimes
    return baseFilters;
  }, [filters]);

  const { data: dataTotals, isFetching: isFetchingTotals } = useQuery({
    queryKey: ['taxRegimeTotals-partner', filtersForTotals],
    queryFn: getQuebrasTaxRegimeTotais,
    enabled: true,
  });

  // Total de empresas geral (sem filtrar por faturamento)
  const totalEmpresas = dataTotals?.total_empresas_geral || 0;

  // Calcular faturamento acumulado total
  const totalFaturamentoAcumulado = useMemo(() => {
    return taxRegimeList.reduce((acc, item) => acc + (item.faturamento_acumulado || 0), 0);
  }, [taxRegimeList]);

  return (
    <div className="flex w-full flex-col gap-4 p-4">
      {/* Header com título */}
      <div className="flex items-center gap-3">
        <ReturnButton previousPage="modulesHome/supervisor/partner" />
        <div className="flex items-center justify-center rounded-full bg-[#CFD8DC] p-3">
          <User color="#37474F" className="size-8" />
        </div>
        <div className="flex flex-col justify-center gap-1">
          <Text fontWeight="bold" fontSize="16px">
            EMPRESAS DO SÓCIO
          </Text>
          <Text fontSize="12px" color="gray.500">
            {partnerData?.nome_socio} - {partnerData?.inscricao}
          </Text>
        </div>
      </div>

      {/* Card de Faturamento Acumulado Total */}
      <Box bg="white" borderRadius="lg" shadow="sm" p={4}>
        <Flex align="center" justify="space-between">
          <Box>
            <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
              Faturamento Acumulado Total
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="blue.600">
              {toCurrency(totalFaturamentoAcumulado)}
            </Text>
            <Text fontSize="xs" color="gray.500" mt={1}>
              Todas as empresas do sócio no ano {currentFilterYear}
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Cards de Status */}
      <TaxRegimeSummaryCards
        totalLucroPresumido={dataTotals?.lucro_presumido ?? 0}
        totalSimplesNacional={dataTotals?.simples_nacional ?? 0}
        totalEmpresas={totalEmpresas}
        totalSimplesGeral={dataTotals?.simples_nacional_geral ?? 0}
        totalPresumidoGeral={dataTotals?.lucro_presumido_geral ?? 0}
        totalSemFaturamento={dataTotals?.total_sem_faturamento ?? 0}
        simplesSemFaturamento={dataTotals?.simples_sem_faturamento ?? 0}
        lucroSemFaturamento={dataTotals?.lucro_sem_faturamento ?? 0}
        currentRegime={currentRegime}
        isTodasFaturadasSelected={false}
        isSemFaturamentoSelected={false}
        isLoading={isFetchingTaxRegimeList}
        isLoadingAlternative={isFetchingTotals}
        onSelectRegime={handleSelectRegime}
        onResetToDefault={undefined}
      />

      {/* Filtros */}
      <TaxRegimeFilters
        filters={filters}
        currentYear={currentFilterYear}
        activeFiltersCount={activeFiltersCount}
        searchMode={searchMode}
        searchValue={
          filters.get('external_id') || filters.get('cnpj') || filters.get('search') || ''
        }
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onPrevYear={handlePrevYear}
        onNextYear={handleNextYear}
        onSelectYear={handleSelectYear}
        onSearchModeChange={setSearchMode}
        onSearchChange={handleSearchChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />

      {/* Lista de empresas */}
      {viewMode === 'table' ? (
        <TaxRegimeTable
          taxRegimeList={taxRegimeList}
          isLoading={isFetchingTaxRegimeList}
          currentRegime={currentRegime}
          onRowClick={handleRowClick}
        />
      ) : isFetchingTaxRegimeList ? (
        <div className="flex flex-col items-center gap-4 py-8">
          <Spinner size="lg" color="primary" />
          <span className="text-xl font-semibold">Carregando...</span>
        </div>
      ) : taxRegimeList?.length === 0 ? (
        <div className="flex flex-col items-center gap-4 py-8">Nenhum item encontrado</div>
      ) : (
        <Box>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {taxRegimeList?.map(item => (
              <TaxRegimeCard
                key={item.external_id}
                company={item}
                onClick={() => handleRowClick(item)}
              />
            ))}
          </SimpleGrid>

          {/* Paginação */}
          <Box mt={4}>
            <Paginator pagination={pagination} />
          </Box>
        </Box>
      )}

      {/* Modal de Detalhes com Gráficos */}
      <TaxRegimeDetailModal
        isOpen={isDetailModalOpen}
        onClose={onDetailModalClose}
        company={selectedCompany}
        currentYear={currentFilterYear}
        socioId={itemId}
      />
    </div>
  );
}
